/**
 * Admin API Client
 * 
 * Connects to the working API-Admin backend
 * Uses the proven authentication endpoints that pass all tests
 * Implements hexagonal error handling for consistent error processing
 */

import { FrontendErrorHandler } from './error-handler';

// IMPORTANT: web-admin uses api-admin (not api-app) for admin-specific operations
const API_URL = import.meta.env.VITE_API_ADMIN_BASE_URL;
if (!API_URL) {
  throw new Error('VITE_API_ADMIN_BASE_URL is not configured');
}

// Error handler instance
const errorHandler = new FrontendErrorHandler();

/**
 * Global Admin API client with automatic auth error handling
 * Redirects to login on 401 errors to handle stale admin sessions
 */
export async function adminApiClient(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const defaultOptions: RequestInit = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(url, defaultOptions);

  // Handle authentication errors globally
  if (response.status === 401) {
    console.warn('Admin authentication failed, redirecting to login');
    window.location.href = '/login';
    throw new Error('Admin authentication required');
  }

  return response;
}

/**
 * Admin login using api-admin auth endpoint
 * Architecture decision: api-admin handles admin-specific authentication
 */
export async function adminLogin(email: string, password: string) {
  try {
    if (!API_URL) {
      throw new Error('API URL not configured - check ENV_KEYS system');
    }

    // Use api-admin auth endpoint for admin authentication
    const response = await fetch(`${API_URL}/api/auth/sign-in/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for httpOnly token
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();
    
    if (response.ok && data.success) {
      // Token is now stored in httpOnly cookie automatically
      // No need to store in localStorage (XSS protection)
      
      return {
        success: true,
        data: data.data
      };
    } else {
      
      // Use error handler to format the error properly
      const errorSummary = errorHandler.formatError(data.error, {
        service: 'admin-api',
        operation: 'login',
      });
      
      const userMessage = errorHandler.getUserMessage(errorSummary);
      
      return {
        success: false,
        error: userMessage
      };
    }
  } catch (error) {
    
    // Use error handler for network/system errors
    const errorSummary = errorHandler.formatError(error, {
      service: 'admin-api',
      operation: 'login',
    });
    
    const userMessage = errorHandler.getUserMessage(errorSummary);
    
    return {
      success: false,
      error: userMessage
    };
  }
}

/**
 * Get current admin profile using api-admin auth endpoint
 */
export async function getAdminProfile() {
  try {
    // Use api-admin auth endpoint for session info
    const response = await adminApiClient('/api/auth/session');

    const data = await response.json();
    
    if (response.ok && data.success) {
      return {
        success: true,
        data: data.data
      };
    } else {
      // Session might be expired - cookie will be cleared by server
      return {
        success: false,
        error: data.error || 'Session expired'
      };
    }
  } catch {
    return {
      success: false,
      error: 'Connection failed'
    };
  }
}

/**
 * Admin logout using api-admin auth endpoint
 */
export async function adminLogout() {
  try {
    // Use api-admin auth endpoint for logout
    await adminApiClient('/api/auth/sign-out', {
      method: 'POST',
      body: JSON.stringify({})
    });
    
    // httpOnly cookie is cleared by server
    return { success: true };
  } catch {
    // Even if API call fails, consider logout successful
    // (httpOnly cookie security doesn't depend on client-side clearing)
    return { success: true };
  }
}

/**
 * Check if user is authenticated
 * Note: With httpOnly cookies, we can't check client-side
 * Authentication status must be verified by calling getAdminProfile()
 */
export function isAuthenticated() {
  // Can't check httpOnly cookie client-side - always return false
  // Use getAdminProfile() to verify authentication status
  return false;
}

/**
 * Get stored token
 * Note: With httpOnly cookies, tokens are not accessible client-side
 */
export function getToken() {
  // httpOnly cookies are not accessible via JavaScript (security feature)
  return null;
}

/**
 * Get current admin (alias for getAdminProfile for compatibility)
 */
export async function getCurrentAdmin() {
  return await getAdminProfile();
}

/**
 * Logout admin (alias for adminLogout for compatibility)
 */
export async function logoutAdmin() {
  return await adminLogout();
}